/**
 * Manufacturer Date Decoder Service
 *
 * This service implements the manufacturer date decoding logic from the Python script.
 * It attempts to decode manufacturer dates from serial numbers based on known patterns
 * for various manufacturers.
 */

// Month letter mappings for different manufacturers
const MONTH_LETTER: Record<string, number> = {
	// A=Jan ... H=Aug, skip I, J=Sep, K=Oct, L=Nov, M=Dec
	A: 1,
	B: 2,
	C: 3,
	D: 4,
	E: 5,
	F: 6,
	G: 7,
	H: 8,
	J: 9,
	K: 10,
	L: 11,
	M: 12,
};

// Some brands use O/N/D for Oct/Nov/Dec in the "month char" position
const MONTH_MITSU: Record<string, number> = {
	...Object.fromEntries(
		Array.from({ length: 9 }, (_, i) => [String(i + 1), i + 1]),
	),
	O: 10,
	N: 11,
	D: 12,
};

/**
 * Helper function to convert two-digit year to full year
 * Heuristic: 00–30 => 2000–2030, otherwise 1900s
 */
function twoDigitYearToFull(yy: number): number {
	return yy <= 30 ? 2000 + yy : 1900 + yy;
}

/**
 * Helper function to convert one-digit year to full year
 * Pick a plausible decade for a single-digit year
 */
function oneDigitYearToFull(y: number, bias?: number): number {
	const now = new Date();
	const currentYear = now.getFullYear();
	const candidates = [2000 + y, 2010 + y, 2020 + y, 1990 + y];

	// Optional bias decade to prefer one window
	if (bias !== undefined) {
		candidates.sort((a, b) => Math.abs(bias + y - a) - Math.abs(bias + y - b));
	}

	for (const c of candidates) {
		if (c <= currentYear + 1) {
			return c;
		}
	}

	return Math.max(...candidates.filter((c) => c <= currentYear), currentYear);
}

/**
 * Helper function to pick from cycle of possible years
 */
function pickFromCycle(candidates: number[]): number {
	const now = new Date();
	const limit = now.getFullYear() + 1;
	const valid = candidates.filter((c) => c <= limit);
	return valid.length > 0 ? Math.max(...valid) : Math.min(...candidates);
}

/**
 * Helper function to get ISO week start date
 */
function isoWeekStart(year: number, week: number): Date {
	try {
		// Simple approximation: first Monday + (week-1)*7
		const jan1 = new Date(year, 0, 1);
		const firstMon = new Date(jan1);
		const daysToAdd = (7 - jan1.getDay()) % 7;
		firstMon.setDate(jan1.getDate() + daysToAdd);

		const result = new Date(firstMon);
		result.setDate(firstMon.getDate() + (week - 1) * 7);
		return result;
	} catch {
		// Fallback to January 1st of the year
		return new Date(year, 0, 1);
	}
}

/**
 * Helper function to check if month is valid
 */
function safeMonth(m: number): boolean {
	return m >= 1 && m <= 12;
}

/**
 * Helper function to extract digits from string
 */
function digits(s: string): string {
	return (s || "").replace(/\D+/g, "");
}

/**
 * Helper function to convert string to uppercase
 */
function upper(s: string): string {
	return (s || "").trim().toUpperCase();
}

/**
 * Generic pattern: YYWW (Year-Week)
 */
function decodeYYWW(sn: string): Date | null {
	const sd = digits(sn);
	if (sd.length < 4) return null;

	const yy = parseInt(sd.substring(0, 2));
	const ww = parseInt(sd.substring(2, 4));

	if (ww >= 1 && ww <= 53) {
		return isoWeekStart(twoDigitYearToFull(yy), ww);
	}
	return null;
}

/**
 * Generic pattern: YYMM (Year-Month)
 */
function decodeYYMM(sn: string): Date | null {
	const sd = digits(sn);
	if (sd.length < 4) return null;

	const yy = parseInt(sd.substring(0, 2));
	const mm = parseInt(sd.substring(2, 4));

	if (safeMonth(mm)) {
		return new Date(twoDigitYearToFull(yy), mm - 1, 1);
	}
	return null;
}

// Manufacturer-specific decoders
const decoders: Record<string, (sn: string) => Date | null> = {
	// APW WYOTT
	"APW WYOTT": (sn: string): Date | null => {
		if (!sn) return null;
		sn = sn.trim();

		// Pattern: YYMM[A-Z]NNNN
		const match = sn.match(/^(\d{2})(\d{2})[A-Z]\d{4,}$/);
		if (match) {
			const year = parseInt(match[1]);
			const month = parseInt(match[2]);
			if (safeMonth(month)) {
				return new Date(twoDigitYearToFull(year), month - 1, 1);
			}
		}

		// Pattern: 6 chars + 4 digits + 3 chars (13 total)
		if (sn.length === 13 && /^[A-Z0-9]{6}\d{4}[A-Z0-9]{3}$/.test(sn)) {
			const yy = parseInt(sn.substring(6, 8));
			const mm = parseInt(sn.substring(8, 10));
			if (safeMonth(mm)) {
				return new Date(twoDigitYearToFull(yy), mm - 1, 1);
			}
		}

		// Pattern: 4 chars + 4 digits + 1 char + 4 chars (13 total)
		if (sn.length === 13 && /^[A-Z0-9]{4}\d{4}[A-Z][A-Z0-9]{4}$/.test(sn)) {
			const mm = parseInt(sn.substring(4, 6));
			const yy = parseInt(sn.substring(6, 8));
			if (safeMonth(mm)) {
				return new Date(2000 + yy, mm - 1, 1);
			}
		}

		return null;
	},

	// DOYON
	DOYON: (sn: string): Date | null => {
		const s = upper(sn);

		// Pattern: YYMM
		const m1 = s.match(/^(\d{2})(\d{2})/);
		if (m1) {
			const yy = parseInt(m1[1]);
			const mm = parseInt(m1[2]);
			if (safeMonth(mm)) {
				return new Date(twoDigitYearToFull(yy), mm - 1, 1);
			}
		}

		// Pattern: YDDD (single digit year + day of year)
		const m2 = s.match(/^(\d)(\d{3})/);
		if (m2) {
			const y = parseInt(m2[1]);
			const ddd = parseInt(m2[2]);
			const yearGuess = oneDigitYearToFull(y);
			try {
				const result = new Date(yearGuess, 0, 1);
				result.setDate(result.getDate() + ddd - 1);
				return result;
			} catch {
				return null;
			}
		}

		return null;
	},

	// JOHNSON CONTROLS
	"JOHNSON CONTROLS": (sn: string): Date | null => {
		const s = upper(sn);
		// Pattern: WW Y Z L NNNNN (10 chars): 2-digit week, next 2 digits are year, then a letter, then 5 digits
		const m = s.match(/^(\d{2})(\d)(\d)[A-Z](\d{5})$/);
		if (m) {
			const week = parseInt(m[1]);
			const year = 2000 + parseInt(m[2] + m[3]);
			if (week >= 1 && week <= 53) {
				return isoWeekStart(year, week);
			}
		}
		return null;
	},

	// AO SMITH
	"AO SMITH": (sn: string): Date | null => {
		const s = upper(sn);
		const m = s.match(/^([A-Z])\s*(\d{2})/);
		if (!m) return null;

		const monCh = m[1];
		const yy = parseInt(m[2]);

		if (!(monCh in MONTH_LETTER)) return null;

		const month = MONTH_LETTER[monCh];
		return new Date(twoDigitYearToFull(yy), month - 1, 1);
	},

	// AMERICAN STANDARD
	"AMERICAN STANDARD": decodeYYWW,
	AMANA: decodeYYWW,

	// BRADFORD WHITE
	"BRADFORD WHITE": (sn: string): Date | null => {
		const s = upper(sn);
		const m = s.match(/^([A-Z])([A-Z])/);
		if (!m) return null;

		const yCh = m[1];
		const mCh = m[2];

		if (!(mCh in MONTH_LETTER)) return null;

		const month = MONTH_LETTER[mCh];

		// Build 20-year cycle anchored to known example windows
		const baseMap: Record<string, number> = {};
		const chars = "ABCDEFGHJKLMNPRSTUWXY"; // skipping I, Q, Z
		chars.split("").forEach((ch, idx) => {
			baseMap[ch] = 1984 + idx;
		});

		if (!(yCh in baseMap)) return null;

		const baseYear = baseMap[yCh];
		const candidates = [baseYear, baseYear + 20, baseYear + 40];
		const year = pickFromCycle(candidates);

		return new Date(year, month - 1, 1);
	},

	// BRYANT, CARRIER, LENNOX, LOCHINVAR, TRANE
	BRYANT: decodeYYWW,
	CARRIER: decodeYYWW,
	TRANE: decodeYYWW,
	LENNOX: (sn: string): Date | null => {
		const sDigits = digits(sn);
		if (sDigits.length >= 7) {
			const yy = parseInt(sDigits.substring(2, 4));
			const mm = parseInt(sDigits.substring(5, 7));
			if (safeMonth(mm)) {
				return new Date(twoDigitYearToFull(yy), mm - 1, 1);
			}
		}

		// Fallback: look for a letter month near the tail and a 2-digit year nearby
		const s = upper(sn);
		const m = s.match(/([A-HJ-M])(\d{2})$/);
		if (m) {
			const mon = MONTH_LETTER[m[1]];
			const yy = parseInt(m[2]);
			return new Date(twoDigitYearToFull(yy), mon - 1, 1);
		}

		return null;
	},
	LOCHINVAR: decodeYYWW,

	// MAGIC CHEF
	"MAGIC CHEF": (sn: string): Date | null => {
		const s = upper(sn);
		const m = s.match(/^([A-Z])\s*(\d)/);
		if (!m) return null;

		const monCh = m[1];
		const yD = parseInt(m[2]);

		if (!(monCh in MONTH_LETTER)) return null;

		const month = MONTH_LETTER[monCh];
		const year = oneDigitYearToFull(yD);

		return new Date(year, month - 1, 1);
	},

	// MITSUBISHI
	MITSUBISHI: (sn: string): Date | null => {
		const s = upper(sn);
		if (s.length < 2) return null;
		if (!/\d/.test(s[0])) return null;

		const yLast = parseInt(s[0]);
		const mCh = s[1];

		if (!(mCh in MONTH_MITSU)) return null;

		const month = MONTH_MITSU[mCh];
		const now = new Date();
		const nowY = now.getFullYear();

		// Prefer 2020s, then 2010s, etc., but never > current_year+1
		const candidates = [2020 + yLast, 2010 + yLast, 2000 + yLast];
		let year = pickFromCycle(candidates);

		// If candidate month this year is far in future, drop to previous decade
		const candDate = new Date(year, month - 1, 1);
		if (candDate > new Date(nowY + 1, 11, 31)) {
			year = Math.max(
				...candidates.filter((c) => c <= nowY),
				candidates[candidates.length - 1],
			);
		}

		return new Date(year, month - 1, 1);
	},

	// RHEEM
	RHEEM: (sn: string): Date | null => {
		const s = upper(sn);

		// Try numeric WWYY (Week-Year)
		const sd = digits(s);
		if (sd.length >= 4) {
			const ww = parseInt(sd.substring(0, 2));
			const yy = parseInt(sd.substring(2, 4));
			if (ww >= 1 && ww <= 53) {
				return isoWeekStart(2000 + yy, ww);
			}
		}

		// Pre-2000 pattern: Letter = Month, Digit = Year
		const m = s.match(/^([A-HJ-M])\s*(\d)/);
		if (m) {
			const mon = MONTH_LETTER[m[1]];
			const year = oneDigitYearToFull(parseInt(m[2]), 1990);
			return new Date(year, mon - 1, 1);
		}

		return null;
	},

	// RUUD (same as Rheem)
	RUUD: (sn: string) => decoders["RHEEM"](sn),

	// RINNAI
	RINNAI: (sn: string): Date | null => {
		const s = upper(sn);

		// Post-2010: YYMM
		const dt = decodeYYMM(s);
		if (dt && dt.getFullYear() >= 2010) {
			return dt;
		}

		// Pre-2010: Digit = Year, Letter = Month
		const m = s.match(/^(\d)([A-HJ-M])/);
		if (m) {
			const year = oneDigitYearToFull(parseInt(m[1]), 2000);
			const mon = MONTH_LETTER[m[2]];
			return new Date(year, mon - 1, 1);
		}

		return null;
	},

	// SAMSUNG
	SAMSUNG: (sn: string): Date | null => {
		const s = upper(sn);
		if (s.length < 9) return null;

		const yearMap: Record<string, number> = {};
		"RSTUVWX".split("").forEach((ch, i) => {
			yearMap[ch] = 2019 + i;
		});

		const monthMap: Record<string, number> = {
			...Object.fromEntries(
				Array.from({ length: 9 }, (_, i) => [String(i + 1), i + 1]),
			),
			A: 10,
			B: 11,
			C: 12,
		};

		const yCh = s[7];
		const mCh = s[8];

		if (yCh in yearMap && mCh in monthMap) {
			return new Date(yearMap[yCh], monthMap[mCh] - 1, 1);
		}

		return null;
	},

	// TRAULSEN, TRUE MANUFACTURING, TURBO AIR
	TRAULSEN: decodeYYMM,
	"TRUE MANUFACTURING": decodeYYMM,
	TRUE: decodeYYMM,
	"TURBO AIR": decodeYYMM,

	// WASSERSTROM (distributor only - cannot decode without OEM scheme)
	WASSERSTROM: () => null,

	// YORK (post-2004 often aligns with JCI)
	YORK: (sn: string) => decoders["JOHNSON CONTROLS"](sn),

	// Additional common manufacturers
	"GENERAL ELECTRIC": decodeYYWW,
	GE: decodeYYWW,
	WHIRLPOOL: decodeYYWW,
	MAYTAG: decodeYYWW,
	KITCHENAID: decodeYYWW,
	FRIGIDAIRE: decodeYYWW,
	ELECTROLUX: decodeYYWW,
	BOSCH: decodeYYWW,
	SIEMENS: decodeYYWW,
	PANASONIC: decodeYYWW,
	LG: decodeYYWW,
	HITACHI: decodeYYWW,
	DAIKIN: decodeYYWW,
	GOODMAN: decodeYYWW,
};

/**
 * Attempts to decode a manufacturer date from a serial number
 *
 * @param manufacturerName - The manufacturer name (case-insensitive)
 * @param serialNumber - The serial number to decode
 * @returns Date object if successfully decoded, null otherwise
 */
export function decodeManufacturerDate(
	manufacturerName: string,
	serialNumber: string,
): Date | null {
	if (!manufacturerName || !serialNumber) {
		return null;
	}

	const manuUpper = manufacturerName.trim().toUpperCase();

	// Try to find a matching decoder
	for (const [key, decoder] of Object.entries(decoders)) {
		if (manuUpper.includes(key)) {
			const decodedDate = decoder(serialNumber);
			if (decodedDate) {
				return decodedDate;
			}
		}
	}

	return null;
}

/**
 * Checks if a manufacturer has a known decoder
 *
 * @param manufacturerName - The manufacturer name to check
 * @returns True if a decoder exists for this manufacturer
 */
export function hasManufacturerDecoder(manufacturerName: string): boolean {
	if (!manufacturerName) return false;

	const manuUpper = manufacturerName.trim().toUpperCase();

	return Object.keys(decoders).some((key) => manuUpper.includes(key));
}
